import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { brl, dateTime, PAYMENT, STATUS } from "@/lib/format";
import { PixKeyBox } from "@/components/store/PixKeyBox";

const STEPS = ["pendente", "aguardando_comprovante", "preparando", "saiu", "concluido"];

export default function OrderStatus() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);

  useEffect(() => {
    const load = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setError(true));
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [id]);

  if (error) return <div className="grid min-h-screen place-items-center text-slate-400">Pedido não encontrado.</div>;
  if (!order) return <div className="grid min-h-screen place-items-center text-slate-400">Carregando...</div>;

  const steps = STEPS.filter((s) => (s !== "saiu" || order.fulfillment !== "retirada")
    && (s !== "aguardando_comprovante" || order.payment_method === "pix"));
  const current = steps.indexOf(order.status);

  return (
    <div className="mx-auto max-w-xl px-4 py-10">
      <div className="rise">
        <CheckCircle2 className="h-12 w-12 text-emerald-400" />
        <p className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-primo-500">Pedido recebido</p>
        <h1 data-testid="order-number" className="font-display text-5xl font-extrabold tracking-tight">#{order.number}</h1>
        <p className="mt-2 text-slate-400">{dateTime(order.created_at)} · {order.fulfillment === "entrega" ? "Entrega" : "Retirada no local"}</p>
      </div>

      <div className="mt-8 rounded-2xl border border-white/[0.08] bg-[#1A1A1A] p-5">
        {order.payment_method === "pix" && order.status !== "cancelado" && order.status !== "concluido" && (
          <div className="mb-4 space-y-3">
            <p data-testid="order-pix-notice" className="rounded-xl border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm font-semibold text-yellow-300">
              Favor enviar o comprovante de pagamento para validar sua compra
            </p>
            <PixKeyBox pixKey={settings?.pix_key} testid="order-pix-key" />
          </div>
        )}
        <p className="text-sm text-slate-400">Status</p>
        <span data-testid="order-status" className={`mt-1 inline-block rounded-full border px-3 py-1 text-sm font-semibold ${STATUS[order.status].cls}`}>{STATUS[order.status].label}</span>
        {order.status !== "cancelado" && (
          <div className="mt-5 flex gap-2">
            {steps.map((s, i) => (
              <div key={s} className="flex-1">
                <div className={`h-1.5 rounded-full ${i <= current ? "bg-primo-500" : "bg-white/10"}`} />
                <p className={`mt-1.5 text-[11px] ${i <= current ? "text-primo-400" : "text-slate-500"}`}>{STATUS[s].label}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 rounded-2xl border border-white/[0.08] bg-[#1A1A1A] p-5 space-y-2 text-sm">
        {order.items.map((i, idx) => (
          <div key={`${i.product_id}-${idx}`}>
            <div className="flex justify-between"><span className="text-slate-300">{i.qty}x {i.name}</span><span>{brl(i.subtotal)}</span></div>
            {(i.selections || []).length > 0 && <p className="pl-4 text-xs text-slate-500">{i.selections.map((s) => `${s.qty}x ${s.option}${s.price ? ` (+${brl(s.price)})` : ""}`).join(", ")}</p>}
          </div>
        ))}
        <div className="border-t border-white/[0.08] pt-2 flex justify-between text-slate-400"><span>Taxa de entrega</span><span>{brl(order.delivery_fee)}</span></div>
        {order.discount > 0 && (
          <div className="flex justify-between text-emerald-400"><span>Desconto {order.coupon_code}</span><span data-testid="order-discount">-{brl(order.discount)}</span></div>
        )}
        <div className="flex justify-between font-display text-xl font-bold"><span>Total</span><span data-testid="order-total" className="text-primo-400">{brl(order.total)}</span></div>
        <p className="text-slate-400">Pagamento: {PAYMENT[order.payment_method]}{order.change_for ? ` · troco para ${brl(order.change_for)}` : ""}</p>
      </div>

      <Link to="/" data-testid="new-order-link" className="mt-8 block rounded-full border border-primo-500/50 py-3 text-center font-display font-bold text-primo-400 hover:bg-primo-500/10">Fazer novo pedido</Link>
    </div>
  );
}
